import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';

const services = [
  {
    icon: 'ri-shield-keyhole-line',
    title: 'IT Security',
    desc: "Ensuring your company's data security is a top priority. With the constant threat of hackers and viruses, having the best protection saves you money and headaches.",
  },
  {
    icon: 'ri-server-line',
    title: 'Hosting Services',
    desc: 'ACU-COMP provides a variety of services to accommodate your company needs. We cover everything from basic website hosting to fully managed cloud services.',
  },
  {
    icon: 'ri-clipboard-line',
    title: 'IT Assessments',
    desc: 'Some of the biggest and most frustrating equipment problems for a company stem from IT issues. Computing errors slow down production, we help fix that.',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud Services',
    desc: 'ACU-COMP offers the latest in infrastructure technology with the Cloud, a tool many companies now use to access their information from anywhere, anytime.',
  },
  {
    icon: 'ri-stack-line',
    title: 'Virtualization',
    desc: 'In an increasingly changing tech environment, virtualization is the logical choice to consolidate any site running multiple older servers into one platform.',
  },
  {
    icon: 'ri-router-line',
    title: 'Networking',
    desc: 'We design, install, and manage the networks that keep your people and systems connected — from LAN and WAN to firewalls, Wi-Fi, and secure remote access.',
  },
  {
    icon: 'ri-cpu-line',
    title: 'IBM Power',
    desc: 'As an IBM Partner, our engineers support and modernize IBM Power environments running AIX, IBM i, and Linux — keeping mission-critical workloads fast and available.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Managed Support',
    desc: '24/7 helpdesk and proactive monitoring so your team keeps working. Our North America-based engineers respond fast when it matters most.',
  },
];

const stats = [
  { value: '20+', label: 'Years in Business' },
  { value: '99.9%', label: 'Uptime Delivered' },
  { value: '24/7', label: 'Helpdesk Coverage' },
];

const industries = [
  { icon: 'ri-hospital-line', name: 'Healthcare' },
  { icon: 'ri-scales-3-line', name: 'Legal' },
  { icon: 'ri-bank-line', name: 'Finance' },
  { icon: 'ri-store-2-line', name: 'Retail' },
  { icon: 'ri-graduation-cap-line', name: 'Education' },
  { icon: 'ri-tools-line', name: 'Manufacturing' },
];

export default function Home() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const resetForm = () => {
    setStatus('idle');
    setErrorMsg('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honey = (fd.get('company_alt') || '').toString().trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }
    fd.delete('company_alt');

    setStatus('submitting');
    setErrorMsg('');
    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, v.toString()));
      body.set('form-name', 'readdy-contact');
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <SiteNavbar home />

      {/* Hero */}
      <section id="top" className="relative min-h-[720px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20abstract%20teal%20and%20deep%20navy%20technology%20background%20with%20soft%20glowing%20network%20lines%2C%20geometric%20light%20trails%2C%20clean%20minimalist%20corporate%20IT%20aesthetic%2C%20subtle%20server%20room%20blur%20in%20distance%2C%20cinematic%20wide%20composition%20with%20luxurious%20blue%20green%20gradient%20mood%20suitable%20for%20professional%20IT%20consulting%20website%20hero&width=1800&height=1000&seq=acu-hero-01&orientation=landscape"
          className="absolute inset-0 w-full h-full object-cover object-top"
          alt="Acu-Comp IT services hero"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/85 via-primary-800/70 to-primary-950/85"></div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8 py-32 text-center">
          <span className="inline-flex items-center gap-2 bg-background-50/10 border border-background-50/20 text-background-50 px-4 py-1.5 rounded-full text-xs font-medium mb-6">
            <i className="ri-map-pin-line"></i> Serving North America
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 leading-tight max-w-4xl mx-auto">
            Solutions that fit <span className="text-accent-500">for everyone.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-background-100/90 max-w-2xl mx-auto">
            Acu-Comp IT Professionals — delivering high quality IT services, cloud, security, and support tailored to your business needs.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#contact"
              className="bg-accent-500 hover:bg-accent-600 text-foreground-950 font-semibold px-8 py-4 rounded-md whitespace-nowrap cursor-pointer transition inline-flex items-center gap-2"
            >
              Get a Free Assessment <i className="ri-arrow-right-line"></i>
            </a>
            <Link
              to="/services"
              className="border border-background-50/40 text-background-50 hover:bg-background-50/10 font-semibold px-8 py-4 rounded-md whitespace-nowrap cursor-pointer transition"
            >
              Explore Services
            </Link>
          </div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-accent-500">{s.value}</div>
                <div className="text-sm text-background-100/80 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-widest">What We Do</span>
            <h2 className="text-3xl md:text-5xl font-bold mt-3 text-foreground-950">
              Complete IT services under one roof
            </h2>
            <p className="text-foreground-600 mt-4">
              From cybersecurity to cloud, our team handles every layer of your technology stack so you can focus on running your business.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div
                key={s.title}
                className="group bg-background-100 hover:bg-background-50 border border-background-200 hover:border-primary-300 rounded-lg p-8 transition cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition">
                  <i className={`${s.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold mt-6 text-foreground-950">{s.title}</h3>
                <p className="text-foreground-600 mt-3 leading-relaxed text-sm">{s.desc}</p>
                <Link to="/services" className="inline-flex items-center gap-1 mt-5 text-primary-600 font-semibold text-sm cursor-pointer whitespace-nowrap">
                  Learn more <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us + Image */}
      <section id="why-us" className="py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=Diverse%20professional%20IT%20consulting%20team%20in%20modern%20bright%20office%20collaborating%20around%20laptop%2C%20clean%20white%20workspace%20with%20subtle%20teal%20accents%2C%20confident%20smiling%20engineers%20and%20business%20people%2C%20natural%20window%20light%2C%20editorial%20corporate%20photography%20with%20soft%20warm%20tones%20and%20organic%20composition&width=1200&height=1000&seq=acu-team-01&orientation=squarish"
              className="w-full h-[520px] object-cover object-top rounded-lg"
              alt="Acu-Comp IT team"
            />
            <div className="absolute -bottom-6 -right-6 bg-accent-500 text-foreground-950 rounded-lg p-6 max-w-xs hidden md:block">
              <div className="text-3xl font-bold">20+ yrs</div>
              <div className="text-sm mt-1">Trusted IT partner for North American businesses since 2004, incorporated since 2008.</div>
            </div>
          </div>
          <div>
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Why Acu-Comp</span>
            <h2 className="text-3xl md:text-5xl font-bold mt-3 text-foreground-950 leading-tight">
              Reliable IT partners who put your needs first.
            </h2>
            <p className="text-foreground-700 mt-5 leading-relaxed">
              Staying connected to the world isn't just important to your company's vitality — it's vital. Whether it's basic email and messaging systems or putting together complex service tools, Acu-Comp is the answer for all of your IT needs.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { i: 'ri-time-line', t: 'Fast Response', d: 'Same-day on-site support across North America.' },
                { i: 'ri-shield-check-line', t: 'Certified Engineers', d: 'Microsoft, IBM and cloud certified specialists.' },
                { i: 'ri-price-tag-3-line', t: 'Transparent Pricing', d: 'Flat monthly plans, no surprise invoices.' },
              ].map((f) => (
                <div key={f.t} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50 shrink-0">
                    <i className={`${f.i} text-xl`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground-950">{f.t}</div>
                    <div className="text-foreground-600 text-sm mt-1">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-secondary-600 font-semibold text-sm uppercase tracking-widest">Industries</span>
            <h2 className="text-3xl md:text-5xl font-bold mt-3 text-foreground-950">
              Tailored IT for every sector
            </h2>
            <p className="text-foreground-600 mt-4">
              Every industry has unique compliance, uptime, and workflow needs. We speak your language.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {industries.map((it) => (
              <div key={it.name} className="bg-secondary-100 hover:bg-secondary-500 hover:text-background-50 text-secondary-900 rounded-lg p-6 text-center cursor-pointer transition">
                <div className="w-12 h-12 flex items-center justify-center mx-auto">
                  <i className={`${it.icon} text-3xl`}></i>
                </div>
                <div className="mt-3 font-semibold text-sm">{it.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Get in Touch</span>
            <h2 className="text-3xl md:text-5xl font-bold mt-3 text-foreground-950 leading-tight">
              Let's talk about your IT roadmap
            </h2>
            <p className="text-foreground-600 mt-4">
              Tell us what your business needs and we'll get back within one business day with a tailored plan.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <div>
                  <div className="text-sm text-foreground-600">Call us</div>
                  <a href="tel:18007800632" className="font-semibold text-foreground-950 cursor-pointer">1-800-780-0632</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <div>
                  <div className="text-sm text-foreground-600">Email us</div>
                  <div className="font-semibold text-foreground-950">inquiry@acu-comp.com</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <div>
                  <div className="text-sm text-foreground-600">Serving</div>
                  <div className="font-semibold text-foreground-950">North America</div>
                </div>
              </div>
            </div>
          </div>

          {status === 'success' ? (
            <div className="bg-background-50 border border-background-200 rounded-lg p-8 md:p-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-checkbox-circle-fill text-4xl"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mt-6 text-foreground-950">
                Thank you — your message is on its way.
              </h3>
              <p className="text-foreground-600 mt-3 leading-relaxed">
                We've received your request and a member of the Acu-Comp team will review it shortly to match you with the right specialist.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 shrink-0">
                    <i className="ri-file-list-3-line"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground-950 text-sm">We review your details</div>
                    <div className="text-foreground-600 text-sm mt-0.5">Your needs are matched with the right engineer.</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 shrink-0">
                    <i className="ri-mail-send-line"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground-950 text-sm">You hear back within one business day</div>
                    <div className="text-foreground-600 text-sm mt-0.5">We'll reply with a tailored plan or a quick question.</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 shrink-0">
                    <i className="ri-calendar-check-line"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground-950 text-sm">Free assessment booked</div>
                    <div className="text-foreground-600 text-sm mt-0.5">We'll schedule a no-obligation review of your setup.</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-foreground-600">
                  Need to reach us sooner? Call{' '}
                  <a href="tel:18007800632" className="font-semibold text-primary-600 cursor-pointer whitespace-nowrap">1-800-780-0632</a>
                </div>
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center justify-center gap-2 border border-background-300 hover:bg-background-100 text-foreground-900 font-semibold px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition"
                >
                  <i className="ri-arrow-go-back-line"></i> Send another message
                </button>
              </div>
            </div>
          ) : (
          <form
            id="acu-contact-form"
            name="readdy-contact"
            method="POST"
            data-netlify="true"
            netlify-honeypot="company_alt"
            onSubmit={handleSubmit}
            className="bg-background-50 border border-background-200 rounded-lg p-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground-800">First Name</label>
                <input
                  name="first_name"
                  required
                  className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground-800">Last Name</label>
                <input
                  name="last_name"
                  required
                  className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-foreground-800">Email</label>
              <input
                type="email"
                name="email"
                required
                className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-primary-500"
              />
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-foreground-800">Company</label>
              <input
                name="company"
                className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-primary-500"
              />
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-foreground-800">Service Interested In</label>
              <select
                name="service"
                className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-primary-500 bg-background-50 pr-8"
              >
                <option>IBM Power Systems</option>
                <option>Servers &amp; Components</option>
                <option>Storage &amp; Data Protection</option>
                <option>Virtualization</option>
                <option>IT Security</option>
                <option>Cloud Services</option>
                <option>Networking &amp; Hosting</option>
                <option>IT Assessments</option>
                <option>Managed IT Support</option>
              </select>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-foreground-800">Message</label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                required
                className="mt-1 w-full text-sm border border-background-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-primary-500"
              ></textarea>
            </div>

            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="mt-6 w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-70 text-background-50 font-semibold py-3 rounded-md whitespace-nowrap cursor-pointer transition"
            >
              {status === 'submitting' ? 'Sending...' : 'Send Message'}
            </button>

            {status === 'error' && (
              <div className="mt-4 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-md p-3">
                {errorMsg}
              </div>
            )}
          </form>
          )}
        </div>
      </section>

      <SiteFooter home />
    </div>
  );
}